// FTEDN Creator — Sentry client-side error tracking
import * as Sentry from '@sentry/react';

let initialized = false;

export function initSentry() {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn || initialized) return;

  Sentry.init({
    dsn,
    environment: import.meta.env.MODE || 'development',
    release: import.meta.env.VITE_GIT_SHA || 'creator-dev',

    // Performance — sample 10% of transactions in production
    tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0,

    // Session replay — capture 1% of sessions, 100% of error sessions
    replaysSessionSampleRate: 0.01,
    replaysOnErrorSampleRate: 1.0,

    // Filter out noisy/expected errors
    beforeSend(event) {
      // Don't send auth redirects
      if (event.exception?.values?.some(v => v.value?.includes('401'))) return null;
      // Don't send user-initiated aborts
      if (event.exception?.values?.some(v => v.value?.includes('AbortError'))) return null;
      return event;
    },

    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
    ],
  });

  initialized = true;
  console.log('✅ Sentry initialized (creator-app)');
}

/** Capture an error with optional context */
export function captureError(error: Error, context?: Record<string, string>) {
  if (context) Sentry.setContext('custom', context);
  Sentry.captureException(error);
}

/** Set the current user for Sentry events */
export function setSentryUser(user: { id: string; email?: string | null; displayName?: string }) {
  Sentry.setUser({ id: user.id, email: user.email ?? undefined, username: user.displayName });
}

/** Clear user on logout */
export function clearSentryUser() {
  Sentry.setUser(null);
}

/** Add breadcrumb for navigation/actions */
export function addBreadcrumb(message: string, category: string = 'action', data?: Record<string, string>) {
  Sentry.addBreadcrumb({ message, category, data, level: 'info' });
}

// Re-export ErrorBoundary for wrapping
export const SentryErrorBoundary = Sentry.ErrorBoundary;
